import { type ReactNode, useMemo } from 'react';

import {
	AssetsIcon,
	AtlassianIcon,
	BitbucketIcon,
	CompassIcon,
	ConfluenceIcon,
	FocusIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	TalentIcon,
	TrelloIcon,
} from '@atlaskit/logo';

// This is the only place we want to import this
import {
	DO_NOT_IMPORT_NewProductsOrder3P,
	DO_NOT_IMPORT_ProductKeys3P,
	DO_NOT_IMPORT_ProductsOrder3P,
	ThirdPartyConfigs,
} from './3p-product-configs';
import { get3PLogo, type RovoLogo, RovoLogoFromLogo } from './icons';
import { AssetsSearchResultIcon } from './icons/assets';
import { AtlasSearchResultIcon } from './icons/atlas-search';
import { CompassSearchResultIcon } from './icons/compass';
import { ConfluenceSearchResultIcon } from './icons/confluence-search';
import { FocusSearchResultIcon } from './icons/focus';
import { JiraProductDiscoverySearchResultIcon } from './icons/jira-product-discovery';
import { JiraSearchResultIcon } from './icons/jira-search';
import { JiraServiceManagementSearchResultIcon } from './icons/jira-service-management';
import { TalentSearchResultIcon } from './icons/talent';
import { TrelloSearchResultIcon } from './icons/trello';
import { type ForgeConnectorProductKey } from './schemas/3p-config';

export interface ProductConfig {
	displayName: ReactNode;
	Icon: RovoLogo;
	IconBorderless?: RovoLogo;
	is3P?: boolean;
	SearchResultIcon?: RovoLogo;
}

const ProductKeys1P = {
	Atlas: 'townsquare',
	Bitbucket: 'bitbucket',
	Compass: 'compass',
	Confluence: 'confluence',
	Jira: 'jira',
	JiraProductDiscovery: 'jira-product-discovery',
	JiraServiceManagement: 'jira-servicedesk',
	Trello: 'trello',
	Focus: 'mercury',
	Assets: 'cmdb',
	// Can't add these until they're ready as we use this type to enforce that product configs are available for all products
	Talent: 'radar',
	// Opsgenie: 'opsgenie',
} as const;

const ProductKeys1PForAnalytics = {
	...ProductKeys1P,
	Loom: 'loom',
};

export const ProductKeys3P: {
    readonly AtlassianWebCrawler: "atlassian-web-crawler";
    readonly Figma: "figma";
    readonly Github: "github";
    readonly GoogleDrive: "drive";
    readonly Loom: "loom";
    readonly OneDrive: "onedrive";
    readonly Dropbox: "dropbox";
    readonly Slack: "slack";
    readonly Sharepoint: "sharepoint";
    readonly Teams: "teams";
    readonly GoogleCalendar: "google-calendar";
    readonly OutlookCalendar: "outlook-calendar";
    readonly OutlookMail: "outlook-mail";
    readonly Gmail: "gmail";
    readonly AdobeSign: "adobe-sign";
    readonly Asana: "asana";
    readonly Zendesk: "zendesk";
    readonly Airtable: "airtable";
    readonly Smartsheet: "smartsheet";
    readonly Notion: "notion";
    readonly Miro: "miro";
    readonly Docusign: "docusign";
    readonly Lucid: "lucid";
    readonly Monday: "monday";
    readonly AzureDevops: "azure-devops";
    readonly Gitlab: "gitlab";
    readonly ConfluenceDC: "confluence-dc";
    readonly Canva: "canva";
    readonly Box: "box";
    readonly Zeplin: "zeplin";
    readonly ServiceNow: "servicenow";
    readonly Freshservice: "freshservice";
    readonly PowerBI: "power-bi";
    readonly ClickUp: "clickup";
    readonly Amplitude: "amplitude";
    readonly Stripe: "stripe";
    readonly AdobeXD: "adobexd";
    readonly Aha: "aha";
    readonly Mural: "mural";
    readonly Webex: "webex";
    readonly Todoist: "todoist";
    readonly PagerDuty: "pagerduty";
    readonly Pipedrive: "pipedrive";
    readonly Hubspot: "hubspot";
    readonly Dovetail: "dovetail";
    readonly LaunchDarkly: "launchdarkly";
    readonly Salesforce: "salesforce";
    readonly Sentry: "sentry";
    readonly JiraDC: "jira-dc";
    readonly Gitbook: "gitbook";
    readonly AWSS3: "aws-s3";
    readonly Databricks: "databricks";
    readonly JsmDC: "jsm-dc";
    readonly ForgeAdamVitareqDev: "forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector";
    readonly ForgeAdamVitareqProd: "forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector";
    readonly ForgeK15tScrollSitesDev: "forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector";
    readonly ForgeK15tScrollSitesProd: "forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector";
    readonly ForgePioAwsDev: "forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector";
    readonly ForgePioAwsProd: "forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector";
    readonly ForgePioClickupDev: "forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector";
    readonly ForgePioClickupProd: "forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector";
    readonly ForgePioGitbookDev: "forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector";
    readonly ForgePioGitbookProd: "forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector";
    readonly ForgePioGithubDev: "forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector";
    readonly ForgePioGithubProd: "forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector";
    readonly ForgePioGitlabDev: "forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector";
    readonly ForgePioGitlabProd: "forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector";
    readonly ForgePioGoogleDriveDev: "forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector";
    readonly ForgePioGoogleDriveProd: "forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector";
    readonly ForgePioGuruDev: "forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector";
    readonly ForgePioGuruProd: "forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector";
    readonly ForgePioHelpjuiceDev: "forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector";
    readonly ForgePioHelpjuiceProd: "forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector";
    readonly ForgePioHubspotDev: "forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector";
    readonly ForgePioHubspotProd: "forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector";
    readonly ForgePioSalesforceDev: "forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector";
    readonly ForgePioSalesforceProd: "forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector";
    readonly ForgePioSharepointDev: "forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector";
    readonly ForgePioSharepointProd: "forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector";
    readonly ForgePioStackOverflowDev: "forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector";
    readonly ForgePioStackOverflowProd: "forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector";
    readonly ForgeUtoolityGitbookDev: "forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector";
    readonly ForgeUtoolityGitbookProd: "forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector";
    readonly ForgeGdriveEugeneTestDev: "forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive";
    readonly ForgeGdriveEugeneTestProd: "forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive";
    readonly ForgeUnitoSalesforceDev: "forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce";
    readonly ForgeUnitoSalesforceProd: "forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce";
    readonly ForgeUnitoServicenowDev: "forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow";
    readonly ForgeUnitoServicenowProd: "forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow";
    readonly ForgeUnitoWrikeDev: "forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike";
    readonly ForgeUnitoWrikeProd: "forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike";
    readonly ForgeUnitoAsanaDev: "forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana";
    readonly ForgeUnitoAsanaProd: "forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana";
    readonly ForgeUnitoBasecampDev: "forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp";
    readonly ForgeUnitoBasecampProd: "forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp";
} = DO_NOT_IMPORT_ProductKeys3P;

export const ProductsOrder3P: import("./3p-product-configs").ProductKeys3P[] = DO_NOT_IMPORT_ProductsOrder3P;
export const NewProductsOrder3P: import("./3p-product-configs").ProductKeys3P[] = DO_NOT_IMPORT_NewProductsOrder3P;

export const ProductKeys: {
    readonly AtlassianWebCrawler: "atlassian-web-crawler";
    readonly Figma: "figma";
    readonly Github: "github";
    readonly GoogleDrive: "drive";
    readonly Loom: "loom";
    readonly OneDrive: "onedrive";
    readonly Dropbox: "dropbox";
    readonly Slack: "slack";
    readonly Sharepoint: "sharepoint";
    readonly Teams: "teams";
    readonly GoogleCalendar: "google-calendar";
    readonly OutlookCalendar: "outlook-calendar";
    readonly OutlookMail: "outlook-mail";
    readonly Gmail: "gmail";
    readonly AdobeSign: "adobe-sign";
    readonly Asana: "asana";
    readonly Zendesk: "zendesk";
    readonly Airtable: "airtable";
    readonly Smartsheet: "smartsheet";
    readonly Notion: "notion";
    readonly Miro: "miro";
    readonly Docusign: "docusign";
    readonly Lucid: "lucid";
    readonly Monday: "monday";
    readonly AzureDevops: "azure-devops";
    readonly Gitlab: "gitlab";
    readonly ConfluenceDC: "confluence-dc";
    readonly Canva: "canva";
    readonly Box: "box";
    readonly Zeplin: "zeplin";
    readonly ServiceNow: "servicenow";
    readonly Freshservice: "freshservice";
    readonly PowerBI: "power-bi";
    readonly ClickUp: "clickup";
    readonly Amplitude: "amplitude";
    readonly Stripe: "stripe";
    readonly AdobeXD: "adobexd";
    readonly Aha: "aha";
    readonly Mural: "mural";
    readonly Webex: "webex";
    readonly Todoist: "todoist";
    readonly PagerDuty: "pagerduty";
    readonly Pipedrive: "pipedrive";
    readonly Hubspot: "hubspot";
    readonly Dovetail: "dovetail";
    readonly LaunchDarkly: "launchdarkly";
    readonly Salesforce: "salesforce";
    readonly Sentry: "sentry";
    readonly JiraDC: "jira-dc";
    readonly Gitbook: "gitbook";
    readonly AWSS3: "aws-s3";
    readonly Databricks: "databricks";
    readonly JsmDC: "jsm-dc";
    readonly ForgeAdamVitareqDev: "forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector";
    readonly ForgeAdamVitareqProd: "forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector";
    readonly ForgeK15tScrollSitesDev: "forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector";
    readonly ForgeK15tScrollSitesProd: "forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector";
    readonly ForgePioAwsDev: "forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector";
    readonly ForgePioAwsProd: "forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector";
    readonly ForgePioClickupDev: "forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector";
    readonly ForgePioClickupProd: "forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector";
    readonly ForgePioGitbookDev: "forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector";
    readonly ForgePioGitbookProd: "forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector";
    readonly ForgePioGithubDev: "forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector";
    readonly ForgePioGithubProd: "forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector";
    readonly ForgePioGitlabDev: "forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector";
    readonly ForgePioGitlabProd: "forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector";
    readonly ForgePioGoogleDriveDev: "forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector";
    readonly ForgePioGoogleDriveProd: "forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector";
    readonly ForgePioGuruDev: "forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector";
    readonly ForgePioGuruProd: "forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector";
    readonly ForgePioHelpjuiceDev: "forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector";
    readonly ForgePioHelpjuiceProd: "forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector";
    readonly ForgePioHubspotDev: "forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector";
    readonly ForgePioHubspotProd: "forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector";
    readonly ForgePioSalesforceDev: "forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector";
    readonly ForgePioSalesforceProd: "forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector";
    readonly ForgePioSharepointDev: "forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector";
    readonly ForgePioSharepointProd: "forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector";
    readonly ForgePioStackOverflowDev: "forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector";
    readonly ForgePioStackOverflowProd: "forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector";
    readonly ForgeUtoolityGitbookDev: "forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector";
    readonly ForgeUtoolityGitbookProd: "forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector";
    readonly ForgeGdriveEugeneTestDev: "forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive";
    readonly ForgeGdriveEugeneTestProd: "forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive";
    readonly ForgeUnitoSalesforceDev: "forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce";
    readonly ForgeUnitoSalesforceProd: "forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce";
    readonly ForgeUnitoServicenowDev: "forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow";
    readonly ForgeUnitoServicenowProd: "forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow";
    readonly ForgeUnitoWrikeDev: "forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike";
    readonly ForgeUnitoWrikeProd: "forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike";
    readonly ForgeUnitoAsanaDev: "forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana";
    readonly ForgeUnitoAsanaProd: "forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana";
    readonly ForgeUnitoBasecampDev: "forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp";
    readonly ForgeUnitoBasecampProd: "forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp";
    readonly Atlas: "townsquare";
    readonly Bitbucket: "bitbucket";
    readonly Compass: "compass";
    readonly Confluence: "confluence";
    readonly Jira: "jira";
    readonly JiraProductDiscovery: "jira-product-discovery";
    readonly JiraServiceManagement: "jira-servicedesk";
    readonly Trello: "trello";
    readonly Focus: "mercury";
    readonly Assets: "cmdb";
    readonly Talent: "radar";
} = {
	...ProductKeys1P,
	...DO_NOT_IMPORT_ProductKeys3P,
} as const;

export type ProductKeys3P =
	(typeof DO_NOT_IMPORT_ProductKeys3P)[keyof typeof DO_NOT_IMPORT_ProductKeys3P];
export type ProductKeys1P = (typeof ProductKeys1P)[keyof typeof ProductKeys1P];
export type ProductKeys1PForAnalytics =
	(typeof ProductKeys1PForAnalytics)[keyof typeof ProductKeys1PForAnalytics];
export type ProductKeys = (typeof ProductKeys)[keyof typeof ProductKeys] | ForgeConnectorProductKey;
export const allProductKeys: ProductKeys[] = Object.values(ProductKeys);
export const all3pProductKeys: ProductKeys3P[] = Object.values(DO_NOT_IMPORT_ProductKeys3P);
export const all1pProductKeys: ProductKeys1P[] = Object.values(ProductKeys1P);
export const is1pProductKey: (key: string) => key is ProductKeys1P = (key: string): key is ProductKeys1P =>
	all1pProductKeys.includes(key as ProductKeys1P);
export const is3pProductKey: (key: string) => key is ProductKeys3P = (key: string): key is ProductKeys3P =>
	all3pProductKeys.includes(key as ProductKeys3P);
export const isProductKey: (key?: string) => key is ProductKeys = (key?: string): key is ProductKeys => {
	if (!key) {
		return false;
	}
	// Check hardcoded keys first
	if (allProductKeys.includes(key as (typeof ProductKeys)[keyof typeof ProductKeys])) {
		return true;
	}
	// Check if it's a Forge connector
	return key.startsWith('forge-connector-');
};

export const productsSupportingEmptyQuery: ProductKeys[] = [ProductKeys.Confluence];

/*
 * The logic here is that if a product key is not in the 1P keys, it must be a 3P key.
 * We can't use `is3pProductKey` as it will impact how we are handling connectors.
 * This is meant to only be used for analytics purposes, to differentiate between 1P and 3P products.
 * */
export const is3pProductKeyForAnalytics: (key: string | undefined) => key is ProductKeys1PForAnalytics = (
	key: string | undefined,
): key is ProductKeys1PForAnalytics =>
	!Object.values(ProductKeys1PForAnalytics).includes(key as ProductKeys1PForAnalytics);

export const PRODUCTS_ORDER: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Compass,
	ProductKeys.Focus,
	ProductKeys.Assets,
	ProductKeys.Talent,
	// ProductKeys.Opsgenie,

	...DO_NOT_IMPORT_ProductsOrder3P,
];

// Updated order to reflect 3P apps popularity as per https://hello.atlassian.net/wiki/x/pyVrVgE.
// TODO: QS-8075 replace into PRODUCTS_ORDER when cleaning up the FG
export const PRODUCTS_ORDER_WITH_3P_BY_POPULARITY: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Compass,
	ProductKeys.Focus,
	ProductKeys.Talent,
	ProductKeys.Assets,
	// ProductKeys.Opsgenie,

	...DO_NOT_IMPORT_NewProductsOrder3P,
];

// This is different to ProductKeys, it represents products we have support for mounting Search Page in
export const PrimaryProductKeys: {
    readonly Atlas: "townsquare";
    readonly Confluence: "confluence";
    readonly Assets: "cmdb";
    readonly Focus: "mercury";
    readonly Jira: "jira";
    readonly Elevate: "elevate";
    readonly Talent: "radar";
    readonly RovoExtension: "rovo-extension";
} = {
	// Fully supported as GA
	Atlas: 'townsquare',
	Confluence: 'confluence',
	// In development
	Assets: 'cmdb',
	Focus: 'mercury',
	Jira: 'jira',
	Elevate: 'elevate',
	Talent: 'radar',
	RovoExtension: 'rovo-extension',
} as const;
export type PrimaryProductKey = (typeof PrimaryProductKeys)[keyof typeof PrimaryProductKeys];

export const getUppercasePrimaryProductKey: (productKey: PrimaryProductKey | undefined) => Uppercase<PrimaryProductKey> | undefined = (
	productKey: PrimaryProductKey | undefined,
): Uppercase<PrimaryProductKey> | undefined =>
	productKey ? (productKey.toUpperCase() as Uppercase<PrimaryProductKey>) : undefined;

/**
 * This is the source of truth for the products that are supported in Full Page Search.
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * For 3P products please see ./3p-product-configs
 */
export const products1P: Record<ProductKeys1P, ProductConfig> = {
	[ProductKeys.Confluence]: {
		displayName: 'Confluence',
		Icon: RovoLogoFromLogo(ConfluenceIcon, 'Confluence'),
		SearchResultIcon: get3PLogo(ConfluenceSearchResultIcon, 'Confluence', 'large'),
	},
	[ProductKeys.Atlas]: {
		displayName: 'Home',
		Icon: RovoLogoFromLogo(AtlassianIcon, 'Home'),
		SearchResultIcon: get3PLogo(AtlasSearchResultIcon, 'Home', 'large'),
	},
	[ProductKeys.Jira]: {
		displayName: 'Jira',
		Icon: RovoLogoFromLogo(JiraIcon, 'Jira'),
		SearchResultIcon: get3PLogo(JiraSearchResultIcon, 'Jira', 'large'),
	},
	[ProductKeys.JiraProductDiscovery]: {
		displayName: 'Jira Product Discovery',
		Icon: RovoLogoFromLogo(JiraProductDiscoveryIcon, 'Jira Product Discovery'),
		SearchResultIcon: get3PLogo(
			JiraProductDiscoverySearchResultIcon,
			'Jira Product Discovery',
			'large',
		),
	},
	[ProductKeys.JiraServiceManagement]: {
		displayName: 'Jira Service Management',
		Icon: RovoLogoFromLogo(JiraServiceManagementIcon, 'Jira Service Management'),
		SearchResultIcon: get3PLogo(
			JiraServiceManagementSearchResultIcon,
			'Jira Service Management',
			'large',
		),
	},
	[ProductKeys.Bitbucket]: {
		displayName: 'Bitbucket',
		Icon: RovoLogoFromLogo(BitbucketIcon, 'Bitbucket'),
		SearchResultIcon: RovoLogoFromLogo(BitbucketIcon, 'large'),
	},
	[ProductKeys.Trello]: {
		displayName: 'Trello',
		Icon: RovoLogoFromLogo(TrelloIcon, 'Trello'),
		SearchResultIcon: get3PLogo(TrelloSearchResultIcon, 'Trello', 'large'),
	},
	[ProductKeys.Compass]: {
		displayName: 'Compass',
		Icon: RovoLogoFromLogo(CompassIcon, 'Compass'),
		SearchResultIcon: get3PLogo(CompassSearchResultIcon, 'Compass', 'large'),
	},
	[ProductKeys.Focus]: {
		displayName: 'Focus',
		Icon: RovoLogoFromLogo(FocusIcon, 'Focus'),
		SearchResultIcon: get3PLogo(FocusSearchResultIcon, 'Focus', 'large'),
	},
	[ProductKeys.Assets]: {
		displayName: 'Assets',
		Icon: RovoLogoFromLogo(AssetsIcon, 'Assets'),
		SearchResultIcon: get3PLogo(AssetsSearchResultIcon, 'Assets', 'large'),
	},
	[ProductKeys.Talent]: {
		displayName: 'Talent',
		Icon: RovoLogoFromLogo(TalentIcon, 'Talent'),
		SearchResultIcon: get3PLogo(TalentSearchResultIcon, 'Talent', 'large'),
	},
};

const getProducts3PFromConfig = (): Record<ProductKeys3P, ProductConfig> => {
	// The use of Partial, the if (config) and the cast here are intentional TS gymnastics
	// We KNOW that this returns a config for all the ProductKeys3P, so we cast that
	// The TS compiler can't know that, and we'd be forced to write UI code like:
	//
	// const Icon = configs[productKey].Icon
	// return Icon && <Icon />
	//
	// Because TS can't prove productKey can be guaranteed to index into configs. But we know it can!
	const thirdPartyProducts = ThirdPartyConfigs;
	const configs: Partial<Record<ProductKeys3P, ProductConfig>> = {};

	all3pProductKeys.map((productKey3P) => {
		const config = thirdPartyProducts.get(productKey3P);
		if (config) {
			configs[productKey3P] = {
				displayName: config.displayName,
				Icon: config.icon,
				IconBorderless: config.iconBorderless,
				is3P: true,
				...(config.searchResultIcon && { SearchResultIcon: config.searchResultIcon }),
			};
		}
	});

	return configs as Record<ProductKeys3P, ProductConfig>;
};

/**
 * This is the source of truth for the products that are supported in Full Page Search for use by the UI
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * If you just need the set of productKeys, please import { allProductKeys } or other similar helpers
 *
 * To get the list of products available to a user (a state concern) please see the exports in store/search
 */
export const useProductConfigs: () => Record<ProductKeys, ProductConfig> = (): Record<ProductKeys, ProductConfig> => {
	return useMemo((): Record<ProductKeys, ProductConfig> => {
		const thirdPartyProducts = getProducts3PFromConfig();

		return {
			...products1P,
			...thirdPartyProducts,
		};
	}, []);
};

export const useProductConfig: (productKey: ProductKeys) => ProductConfig = (productKey: ProductKeys): ProductConfig => {
	return useProductConfigs()[productKey];
};

// Used by SAIN to detrermine knowledge source filtering
enum KnowledgeSourceTypeValues {
	GITHUB = 'github',
	CONFLUENCE = 'confluence',
	JIRA = 'jira',
	ATLAS = 'atlas',
	HELP_AGGREGATOR = 'help_aggregator',
	GOOGLE_DRIVE = 'google_drive',
	MICROSOFT_SHAREPOINT = 'microsoft_sharepoint',
	SLACK = 'slack',
	MICROSOFT_TEAMS = 'teams',
	FIGMA = 'figma',
	CONFLUENCE_DC = 'confluence-dc',
	ATLASSIAN_WEB_CRAWLER = 'atlassian-web-crawler',
	LOOM = 'loom',
	GOOGLE_CALENDAR = 'google_calendar',
	ASSETS = 'cmdb',
	SALESFORCE = 'salesforce',
}

export const MapProductToKnowledgeSources: { [key: string]: string } = {
	[ProductKeys.Confluence]: KnowledgeSourceTypeValues.CONFLUENCE,
	[ProductKeys.Github]: KnowledgeSourceTypeValues.GITHUB,
	[ProductKeys.Jira]: KnowledgeSourceTypeValues.JIRA,
	[ProductKeys.Atlas]: KnowledgeSourceTypeValues.ATLAS,
	[ProductKeys.GoogleDrive]: KnowledgeSourceTypeValues.GOOGLE_DRIVE,
	[ProductKeys.Sharepoint]: KnowledgeSourceTypeValues.MICROSOFT_SHAREPOINT,
	[ProductKeys.Slack]: KnowledgeSourceTypeValues.SLACK,
	[ProductKeys.Teams]: KnowledgeSourceTypeValues.MICROSOFT_TEAMS,
	[ProductKeys.Figma]: KnowledgeSourceTypeValues.FIGMA,
	[ProductKeys.ConfluenceDC]: KnowledgeSourceTypeValues.CONFLUENCE_DC,
	[ProductKeys.AtlassianWebCrawler]: KnowledgeSourceTypeValues.ATLASSIAN_WEB_CRAWLER,
	[ProductKeys.Loom]: KnowledgeSourceTypeValues.LOOM,
	[ProductKeys.GoogleCalendar]: KnowledgeSourceTypeValues.GOOGLE_CALENDAR,
	[ProductKeys.Assets]: KnowledgeSourceTypeValues.ASSETS,
	[ProductKeys.Salesforce]: KnowledgeSourceTypeValues.SALESFORCE,
};
